import { useFrontendTool } from "@copilotkit/react-core/v2";
import { z } from "zod";
import App from "app";
import api from "core/api/api";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import metaObjectConverter from "core/services/metaStoreService/metaobject-converter";
import statusManagement from "src/status-management";

/**
 * A single tool that updates any combination of App Settings fields which have no TQL equivalent
 * at all (recovery interval, encryption, auto-resume, Java packages, data validation, exception
 * handling). They're updated directly via the same `UPDATEAPPLICATIONSETTINGS` RPC the App Settings
 * panel uses (see `core/api/api.js` `updateApplicationSettings`) — one tool, one fetch, one persist
 * call for the plain fields, regardless of how many change. Adding a new plain field means adding
 * one entry to `parameters` below and one line to the `applyFieldUpdates` mapping — not a new tool.
 *
 * `enable_data_validation` is handled separately (see `handleDataValidationChange`) because, unlike
 * every other field here, it is NOT part of `updateApplicationSettings` — it requires its own
 * `api.setEnableDataValidation` RPC and an eligibility check. `ai:reload-graph` is triggered once,
 * at the very end, after the data validation change too, so the panel picks up every requested
 * change in a single refresh rather than firing multiple times.
 */

const EXCEPTION_TYPES = [
    "AdapterException",
    "ArithmeticException",
    "ClassCastException",
    "ConnectionException",
    "InvalidDataException",
    "NullPointerException",
    "NumberFormatException",
    "SystemException",
    "UnExpectedDDLException",
    "UnknownException"
];

const exceptionAction = z.enum(["IGNORE", "STOP"]);

const exceptionHandlersShape = {
    AdapterException: exceptionAction.optional(),
    ArithmeticException: exceptionAction.optional(),
    ClassCastException: exceptionAction.optional(),
    ConnectionException: exceptionAction.optional(),
    InvalidDataException: exceptionAction.optional(),
    NullPointerException: exceptionAction.optional(),
    NumberFormatException: exceptionAction.optional(),
    SystemException: exceptionAction.optional(),
    UnExpectedDDLException: exceptionAction.optional(),
    UnknownException: exceptionAction.optional()
};

const parameters = z.object({
    app_name: z.string().describe("Fully qualified app name (e.g., 'admin.MyApp')"),
    recovery_interval_seconds: z
        .number()
        .int()
        .nonnegative()
        .optional()
        .describe(
            "Recovery interval in seconds — always seconds, never minutes/hours. Convert the user's " +
                "stated unit before passing this (e.g. '2 minutes' -> 120, '1 hour' -> 3600). 0 disables recovery."
        ),
    encrypted: z
        .boolean()
        .optional()
        .describe("True to enable stream encryption (AES-256), false to disable."),
    enable_auto_resume: z
        .boolean()
        .optional()
        .describe("True to enable auto-resume, false to disable it."),
    autoresume_retry_count: z
        .number()
        .int()
        .nonnegative()
        .optional()
        .describe(
            "Auto-resume retry attempts. Only meaningful when enable_auto_resume=true; defaults to 2 if omitted."
        ),
    autoresume_retry_interval_seconds: z
        .number()
        .int()
        .nonnegative()
        .optional()
        .describe(
            "Seconds between auto-resume retries — always seconds, convert minutes/hours before calling. " +
                "Only meaningful when enable_auto_resume=true; defaults to 60 if omitted."
        ),
    import_statements: z
        .array(z.string())
        .optional()
        .describe(
            "Full REPLACEMENT list of Java import statements (the App Settings 'Java Package' list). " +
                "If the user wants to add one package, first read the app's current list and include it " +
                "plus the new entry — this replaces the whole list, it does not append."
        ),
    enable_data_validation: z
        .boolean()
        .optional()
        .describe(
            "True to enable Data Validation, false to disable it. Not every app is eligible — if the app " +
                "doesn't support it, this returns an error instead of applying the change."
        ),
    exception_handlers: z
        .object(exceptionHandlersShape)
        .partial()
        .optional()
        .describe(
            "Per-exception-type handling (the App Settings 'Exception Handling' section). Each key is one " +
                `of ${EXCEPTION_TYPES.join(", ")}, value is "IGNORE" (skip and continue) or "STOP" (halt ` +
                "processing). This is a MERGE, not a replace — only include the exception types the user " +
                "actually wants changed; unmentioned types keep their current setting."
        )
});

async function fetchAppModel(appName: string) {
    const fullId = metaObjectConverter.ensureFullId(appName.trim(), metaStoreService.entities.APPLICATION);
    return metaStoreService.findById(fullId);
}

async function handleDataValidationChange(appModel: any, enabled: boolean) {
    if (enabled) {
        const isEligible = await api.isEligibleForDataValidation(appModel.get("id"));
        if (!isEligible) {
            throw new Error("This application is not eligible for Data Validation.");
        }
    }
    await api.setEnableDataValidation(appModel.get("id"), enabled);
}

function applyFieldUpdates(appModel: any, fields: z.infer<typeof parameters>) {
    const applied: Record<string, unknown> = {};

    if (fields.recovery_interval_seconds !== undefined) {
        appModel.set("recoveryPeriod", fields.recovery_interval_seconds);
        applied.recovery_interval_seconds = fields.recovery_interval_seconds;
    }
    if (fields.encrypted !== undefined) {
        appModel.set("encrypted", fields.encrypted);
        applied.encrypted = fields.encrypted;
    }
    if (fields.enable_auto_resume !== undefined) {
        const retryCount = fields.enable_auto_resume ? fields.autoresume_retry_count ?? 2 : 0;
        const retryInterval = fields.enable_auto_resume ? fields.autoresume_retry_interval_seconds ?? 60 : 0;
        appModel.set("enableAutoResume", fields.enable_auto_resume);
        appModel.set("autoResumeRetryCount", retryCount);
        appModel.set("autoResumeRetryInterval", retryInterval);
        applied.enable_auto_resume = fields.enable_auto_resume;
        applied.autoresume_retry_count = retryCount;
        applied.autoresume_retry_interval_seconds = retryInterval;
    }
    if (fields.import_statements !== undefined) {
        appModel.set("importStatements", fields.import_statements);
        applied.import_statements = fields.import_statements;
    }
    if (fields.exception_handlers !== undefined) {
        const merged = { ...(appModel.get("eHandlers") ?? {}), ...fields.exception_handlers };
        appModel.set("eHandlers", merged);
        applied.exception_handlers = merged;
    }

    return applied;
}

export const useApplicationSettingsTools = () => {
    useFrontendTool({
        name: "update_application_settings_tool",
        description:
            "Update one or more App Settings fields on an already-created Striim application: recovery " +
            "interval, encryption, auto-resume, Java packages, Data Validation, and/or exception handling. " +
            "None of these are expressible via TQL (there is no `ALTER APPLICATION ... RECOVERY ...` etc.) — " +
            "this updates the application's settings directly, the same mechanism the App Settings panel " +
            "itself uses. Pass only the fields the user actually wants changed; omit the rest.",
        parameters,
        handler: async ({ app_name, enable_data_validation, ...fields }: z.infer<typeof parameters>) => {
            const appName = app_name.trim();
            if (!appName.includes(".")) {
                return {
                    error: `Invalid app_name '${appName}'. Must be in NAMESPACE.APPNAME format (e.g., 'admin.MyApp')`,
                    error_type: "ValidationError"
                };
            }

            if (Object.keys(fields).length === 0 && enable_data_validation === undefined) {
                return {
                    error: "No settings provided to update.",
                    error_type: "ValidationError"
                };
            }

            try {
                const appFullId = metaObjectConverter.ensureFullId(appName, metaStoreService.entities.APPLICATION);
                const statusResult = await api.getApplicationStatus([appFullId]);
                const status = statusResult?.[appFullId];
                if (status && statusManagement.isTransientStatus(status)) {
                    return {
                        error:
                            `Cannot update settings for '${appName}' — it is currently ` +
                            `${statusManagement.getStatusDisplayName(status)}. Wait for it to settle and try again.`,
                        error_type: "InvalidStateError"
                    };
                }
                if (status && !statusManagement.isApplicationCreated(status)) {
                    return {
                        error:
                            `Cannot update settings for '${appName}' — it is currently ` +
                            `${statusManagement.getStatusDisplayName(status)}. Undeploy the app first; ` +
                            "App Settings can only be changed while the app is Created (not yet deployed).",
                        error_type: "InvalidStateError"
                    };
                }

                const appModel = await fetchAppModel(appName);
                const applied = applyFieldUpdates(appModel, { app_name: appName, ...fields });
                await api.updateApplicationSettings(appModel.attributes);

                if (enable_data_validation !== undefined) {
                    await handleDataValidationChange(appModel, enable_data_validation);
                    applied.enable_data_validation = enable_data_validation;
                }

                App.vent.trigger("ai:reload-graph");
                return { success: true, app_name: appName, ...applied };
            } catch (e) {
                const message = e instanceof Error ? e.message : String(e);
                return { error: message, error_type: "StriimAPIError" };
            }
        }
    });
};
